/**
 * One ticket: the conversation on the left, what the ticket *is* on the right.
 *
 * The same split `TaskDetail` uses, for the same reason - the thread is the part
 * that grows and the record is the part that stays a fixed height - and
 * `items-start`, so a ticket with two messages does not stretch the properties
 * card into a column of nothing.
 *
 * Status sits in the page head rather than in the right column: it is the one
 * field an operator changes on the way past, and burying it in a stack of four
 * menus makes the commonest action the hardest one to find.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft, LifeBuoy, ListTodo, Loader2, MessageSquareText, Sprout, Users, Voicemail,
} from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { hostSiteId, userCanPlatform } from "../../../lib/auth";
import {
  deleteTicket, deleteTicketMessage, getCannedReplies, getDepartments, getTicket,
  ticketParty, updateTicket,
  type CannedReply, type Ticket, type TicketMessage, type TicketPriority,
  type TicketStatus, type TicketTask,
} from "../../../lib/support";
import { staffOptionLabel } from "../../../lib/staff";
import { ticketSourceLabel } from "../../../lib/ticketSource";
import {
  Btn, Card, CardHead, ComboField, EmptyState, MenuField, PageHead, fmtDateTime, useToast,
} from "../../ui";
import { PopHost } from "../../shell/Pop";
import { useCrmUser } from "../../shell/CrmUser";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { useStaff } from "../_shared/staff";
import { TagChips } from "../_shared/formBits";
import { TAGS_MAX, TAG_MAX_LEN } from "../projects/projectFields";
import { ReplyBox } from "./ReplyBox";
import { TicketCallBadge } from "./TicketCallBadge";
import { TicketHeadMenu } from "./TicketHeadMenu";
import { TicketTaskModal } from "./TicketTaskModal";
import { TicketTasks } from "./TicketTasks";
import { TicketThread } from "./TicketThread";
import { SlaLines } from "./SlaBadge";
import { PRIORITIES, STATUSES } from "./tone";

type FullTicket = Ticket & { messages: TicketMessage[] };

/**
 * The right this page needs before it will offer to file a ticket as a task.
 *
 * NOT the right that opens the support section - creating a task writes to the
 * projects register, which is a separate permission an agent can perfectly well
 * be without. The API checks the same key (`ticket-tasks.routes.ts`), so this is
 * only about not drawing a button whose one possible answer is 403.
 */
const TASK_PERM = "manage:crm.projects";

/** `crm-card` is a surface and nothing else, so a body says where its edges are. */
const BODY = "px-6 pb-5 pt-3";

/** One label-and-value line. Shared with `TaskDetail` in shape so a ticket and a
 *  task read the same way down the right-hand column. */
function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center gap-3 border-t border-[var(--crm-hair)] py-2.5 text-[13px] first:border-t-0 first:pt-0">
      <span className="crm-mut">{label}</span>
      <span className="crm-push crm-tnum font-semibold">{value}</span>
    </div>
  );
}

/** A label above an editable control, stacked. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <span className="crm-lab">{label}</span>
      {children}
    </div>
  );
}

const UNASSIGNED = "none";

/**
 * The department combobox, holding its own draft.
 *
 * The input this replaced wrote on blur, and that rule has to survive the swap:
 * a free-text field that PATCHes per keystroke is a PATCH per letter. So the
 * typing stays local and only two things commit - picking a suggestion, and
 * leaving the field with something different in it than the ticket holds.
 *
 * Keyed on the incoming value by the caller (`key={...}`) is NOT how it stays in
 * sync; it re-seeds from the prop instead, so a ticket reloaded under it does
 * not strand an old draft.
 */
function DepartmentField({
  value,
  options,
  label,
  placeholder,
  onCommit,
}: {
  value: string;
  options: string[];
  label: string;
  placeholder: string;
  onCommit: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  return (
    <div
      onBlur={(e) => {
        // Only when focus has really left the control - moving from the input to
        // its own suggestion list is not leaving it.
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        const next = draft.trim();
        if (next !== value) onCommit(next);
      }}
    >
      <ComboField
        value={draft}
        onChange={setDraft}
        // Picking is a decision and saves at once. It also leaves focus in the
        // input, so waiting for a blur would leave the choice unwritten until
        // the operator clicked somewhere else.
        onPick={(v) => v !== value && onCommit(v)}
        options={options}
        label={label}
        placeholder={placeholder}
      />
    </div>
  );
}

export function TicketDetail() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<FullTicket | null>(null);
  // Shared with the queue behind it, so arriving here from the list does not ask
  // for the directory again - and so this picker gets the one order the app puts
  // people in (see `_shared/staff.ts`).
  const { staff } = useStaff();
  const [canned, setCanned] = useState<CannedReply[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState<TicketMessage | null>(null);
  const [taskOpen, setTaskOpen] = useState(false);
  const user = useCrmUser();
  const toast = useToast();
  const canMakeTask = userCanPlatform(user, TASK_PERM);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    getTicket(id)
      .then(setTicket)
      .catch(() => setTicket(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    getCannedReplies()
      .then((r) => setCanned(r.rows))
      .catch(() => undefined);
    getDepartments()
      .then((r) => setDepartments(r.rows))
      .catch(() => undefined);
  }, []);

  if (loading) {
    return (
      <div className="crm-card flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  if (!ticket) {
    return <EmptyState icon={LifeBuoy} title={t("support.notFound")} />;
  }

  // Both clocks `na` means this priority carries no promise, and the whole block
  // - heading included - stays off the page. A heading over nothing is a label
  // explaining an absence on every ticket of that priority forever.
  const slaShown =
    Boolean(ticket.sla) &&
    !(ticket.sla!.firstResponse.state === "na" && ticket.sla!.resolve.state === "na");

  async function patch(body: Record<string, unknown>) {
    if (!ticket) return;
    // The response is taken rather than the local guess kept: the server stamps
    // `resolvedAt` and `firstResponseAt` itself, and both are shown below.
    const updated = await updateTicket(ticket.id, body);
    setTicket((prev) => (prev ? { ...prev, ...updated } : prev));
  }

  /**
   * A task has just been filed. It goes onto the ticket in hand rather than
   * triggering a reload: the response IS the row this card draws, and re-reading
   * the ticket would redraw the whole conversation - including the reply box the
   * operator may have half-written - to add one line.
   */
  function addTask(task: TicketTask) {
    setTicket((prev) => (prev ? { ...prev, tasks: [task, ...(prev.tasks ?? [])] } : prev));
    toast(t("support.taskCreated"));
  }

  async function removeMessage(m: TicketMessage) {
    if (!ticket) return;
    try {
      await deleteTicketMessage(ticket.id, m.id);
      setTicket((prev) => (prev ? { ...prev, messages: prev.messages.filter((x) => x.id !== m.id) } : prev));
    } finally {
      setConfirmMsg(null);
    }
  }

  // The party the ticket is filed against, whichever table it lives in. The
  // contact name is the last resort and is not a link - it is a string somebody
  // typed, not a record.
  const party = ticketParty(ticket);
  const who = party?.name ?? ticket.contactName ?? t("support.noClient");

  return (
    <PopHost>
      {/* A real Link, not a button that navigates: back is a place, and the
          browser's middle-click and history belong to it. */}
      <Link to="/support" className="crm-mut mb-3 inline-flex items-center gap-1 text-[12.5px] hover:text-foreground">
        <ChevronLeft className="size-3.5" />
        {t("support.back")}
      </Link>

      <PageHead
        title={ticket.subject}
        sub={
          <span className="crm-tnum">
            {ticket.number} · {who} · {fmtDateTime(ticket.createdAt)}
          </span>
        }
        actions={
          <>
            {/* First, and the only action here that is about doing the work
                rather than about filing the conversation. Hidden - not disabled -
                for an operator without the projects right: a greyed-out button is
                a promise the CRM cannot keep for them. */}
            {canMakeTask ? (
              <Btn onClick={() => setTaskOpen(true)}>
                <ListTodo className="size-4" />
                {t("support.makeTask")}
              </Btn>
            ) : null}
            <MenuField
              value={ticket.status}
              onChange={(s: TicketStatus) => void patch({ status: s })}
              label={t("support.statusCol")}
              options={STATUSES.map((s) => ({ value: s, label: t(`support.statusLabel.${s}`) }))}
            />
            {/* Delete is behind the kebab rather than out here, and the reason
                is argued in the menu's own file: a red pill on the strip was the
                control that wrapped onto a second row. */}
            <TicketHeadMenu onDelete={() => setConfirmDel(true)} />
          </>
        }
      />

      <div className="crm-cols-ab items-start">
        <Card>
          <CardHead
            title={
              <span className="flex items-center gap-1.5">
                <MessageSquareText className="size-3.5" />
                {t("support.conversation")}
              </span>
            }
            /* No status pill here. The menu in the page head above is already
               showing the same word, and a state stated twice on one screen
               makes neither copy look like the one that is true. */
          />
          <div className={BODY}>
            <TicketThread messages={ticket.messages ?? []} staff={staff} onDelete={setConfirmMsg} />
            <div className="mt-4">
              <ReplyBox ticketId={ticket.id} canned={canned} siteId={hostSiteId(user)} onSent={load} />
            </div>
          </div>
        </Card>

        <div className="flex flex-col gap-4">
          <Card>
            <CardHead title={t("support.properties")} />
            <div className={`${BODY} flex flex-col gap-1`}>
              <Field label={t("support.priority")}>
                <MenuField
                  value={ticket.priority}
                  onChange={(p: TicketPriority) => void patch({ priority: p })}
                  label={t("support.priority")}
                  options={PRIORITIES.map((p) => ({ value: p, label: t(`support.priorityLabel.${p}`) }))}
                  className="w-full"
                />
              </Field>
              <Field label={t("support.assignee")}>
                <MenuField
                  value={ticket.assignedTo ?? UNASSIGNED}
                  onChange={(v) => void patch({ assignedTo: v === UNASSIGNED ? null : v })}
                  label={t("support.assignee")}
                  // People who have left stay on the list, last and marked:
                  // the ticket in front of you may already be assigned to one
                  // of them, and a name that vanished from the menu would read
                  // as a broken field rather than as somebody who has gone.
                  options={[
                    { value: UNASSIGNED, label: t("support.unassigned") },
                    ...staff.map((s) => ({
                      value: s.id,
                      label: staffOptionLabel(s, t("common.inactive")),
                    })),
                  ]}
                  className="w-full"
                />
              </Field>
              <Field label={t("support.department")}>
                {/* A combobox over what is already in use, but still free text:
                    the column has always been a string and every historical
                    ticket holds whatever was typed into it. */}
                <DepartmentField
                  value={ticket.department ?? ""}
                  options={departments}
                  label={t("support.department")}
                  placeholder={t("support.departmentPlaceholder")}
                  onCommit={(v) => void patch({ department: v || null })}
                />
              </Field>
              {/* Beside the department and not instead of it: a department is
                  WHO owns the thread and there is exactly one, a label is what
                  the thread is ABOUT and there can be several. Folding the two
                  into one control would force a desk to choose between routing
                  a ticket and describing it. */}
              <Field label={t("support.tags")}>
                <TagChips
                  tags={ticket.tags}
                  onChange={(next) => void patch({ tags: next })}
                  placeholder={t("support.tagsPlaceholder")}
                  maxLen={TAG_MAX_LEN}
                  maxCount={TAGS_MAX}
                  removeLabel={t("common.remove")}
                />
              </Field>
            </div>
          </Card>

          {/* The work this ticket turned into, above the provenance below it:
              "what has been done about this" is the question somebody reopening
              a thread has, and where it came from is the answer to a rarer one.
              Draws nothing when there are no tasks. */}
          <TicketTasks tasks={ticket.tasks ?? []} />

          {/* Only on a ticket that came from a call, which today means a
              voicemail. A card that says "no call" on every ticket opened by
              hand would be four lines of nothing on most screens. */}
          {ticket.callLog ? (
            <Card>
              <CardHead
                title={
                  <span className="flex items-center gap-1.5">
                    <Voicemail className="size-3.5" />
                    {t("support.fromCall")}
                  </span>
                }
              />
              <div className={BODY}>
                <TicketCallBadge call={ticket.callLog} />
              </div>
            </Card>
          ) : null}

          <Card>
            <CardHead title={t("support.details")} />
            {/* Only what the card above does not already carry. The first draft
                repeated priority and the assignee here, so the right column
                stated the same two facts twice - once as a menu and once as a
                pill - and neither one looked authoritative. */}
            <div className={BODY}>
              <Row
                label={t(party?.kind === "lead" ? "support.lead" : "support.client")}
                value={
                  party ? (
                    <Link
                      to={party.kind === "lead" ? `/leads/${party.id}` : `/clients/${party.id}`}
                      className="inline-flex items-center gap-1.5 hover:underline"
                    >
                      {party.kind === "lead" ? (
                        <Sprout className="size-3.5 text-muted-foreground" />
                      ) : (
                        <Users className="size-3.5 text-muted-foreground" />
                      )}
                      {party.name}
                    </Link>
                  ) : (
                    who
                  )
                }
              />
              {ticket.contactEmail ? (
                <Row
                  label={t("support.contactEmail")}
                  value={
                    <a href={`mailto:${ticket.contactEmail}`} className="hover:underline">
                      {ticket.contactEmail}
                    </a>
                  }
                />
              ) : null}
              {/* Translated rather than printed raw: the column holds the word
                  the creating code used ("voicemail", "form"), which is a key,
                  not a sentence in either interface language. */}
              <Row
                label={t("support.source")}
                value={ticket.source ? ticketSourceLabel(ticket.source, t) : "-"}
              />
              <Row label={t("support.created")} value={fmtDateTime(ticket.createdAt)} />
              {ticket.firstResponseAt ? (
                <Row label={t("support.firstResponse")} value={fmtDateTime(ticket.firstResponseAt)} />
              ) : null}
              {ticket.resolvedAt ? (
                <Row label={t("support.resolvedAt")} value={fmtDateTime(ticket.resolvedAt)} />
              ) : null}
              {/* Under the two stamps it is about, not above them: the clocks
                  are a reading OF those timestamps, and putting the verdict
                  first would make somebody scroll back up to see what it was
                  measured from. Absent entirely when this priority carries no
                  promise - see SlaBadge.tsx. */}
              {slaShown ? (
                <div className="border-t border-[var(--crm-hair)] pt-2.5">
                  <span className="crm-lab">{t("support.sla.title")}</span>
                  <SlaLines sla={ticket.sla} />
                </div>
              ) : null}
            </div>
          </Card>
        </div>
      </div>

      {taskOpen ? (
        <TicketTaskModal
          ticket={ticket}
          actorId={user.id}
          onClose={() => setTaskOpen(false)}
          onCreated={addTask}
        />
      ) : null}

      {confirmDel ? (
        <ConfirmModal
          title={t("support.deleteTitle")}
          body={t("support.deleteConfirm")}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await deleteTicket(ticket.id);
            navigate("/support");
          }}
          onClose={() => setConfirmDel(false)}
        />
      ) : null}

      {confirmMsg ? (
        <ConfirmModal
          title={t("support.msgDeleteTitle")}
          body={t("support.msgDeleteConfirm")}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => removeMessage(confirmMsg)}
          onClose={() => setConfirmMsg(null)}
        />
      ) : null}
    </PopHost>
  );
}
